import {
  CONSENT_CHANGE_EVENT,
  hasAnalyticsConsent,
  hasMarketingConsent,
} from "./cookieConsent";

const GTM_ID = "GTM-5J8SSF37";
const ADSENSE_CLIENT = "ca-pub-4712744752191797";
const MAX_QUEUED_EVENTS = 100;

let ready = false;
let started = false;
let posthog = null;
let posthogLoading = null;
let queuedEvents = [];

const appendScript = (id, src, crossOrigin) => {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  if (crossOrigin) script.crossOrigin = crossOrigin;
  document.head.appendChild(script);
};

const updateGoogleConsent = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("consent", "update", {
    analytics_storage: hasAnalyticsConsent() ? "granted" : "denied",
    ad_storage: hasMarketingConsent() ? "granted" : "denied",
    ad_user_data: hasMarketingConsent() ? "granted" : "denied",
    ad_personalization: hasMarketingConsent() ? "granted" : "denied",
  });
};

const loadPostHog = () => {
  const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  if (!token || posthogLoading || posthog) return;
  posthogLoading = import("posthog-js")
    .then(({ default: client }) => {
      client.init(token, {
        api_host: import.meta.env.VITE_POSTHOG_HOST,
        defaults: "2026-05-30",
      });
      posthog = client;
      if (!hasAnalyticsConsent()) {
        posthog.opt_out_capturing();
        queuedEvents = [];
        return;
      }
      queuedEvents.forEach(([name, properties]) => posthog.capture(name, properties));
      queuedEvents = [];
    })
    .catch((error) => {
      console.error("PostHog failed to load:", error);
      posthogLoading = null;
    });
};

const loadAllowedServices = () => {
  updateGoogleConsent();
  if (!ready) return;

  if (hasAnalyticsConsent()) {
    if (!document.getElementById("kewordal-gtm")) {
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      appendScript(
        "kewordal-gtm",
        `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`,
      );
    }
    if (posthog) posthog.opt_in_capturing();
    loadPostHog();
  } else {
    queuedEvents = [];
    if (posthog) posthog.opt_out_capturing();
  }

  if (hasMarketingConsent()) {
    appendScript(
      "kewordal-adsense",
      `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`,
      "anonymous",
    );
  }
};

export const capturePostHog = (name, properties) => {
  if (!hasAnalyticsConsent()) return;
  if (posthog) {
    posthog.capture(name, properties);
    return;
  }
  if (queuedEvents.length < MAX_QUEUED_EVENTS) {
    queuedEvents.push([name, properties]);
  }
};

export const startOptionalServices = () => {
  if (started) return;
  started = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  updateGoogleConsent();
  window.addEventListener(CONSENT_CHANGE_EVENT, loadAllowedServices);

  const afterPaint = () => {
    const onIdle = () => {
      ready = true;
      loadAllowedServices();
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(onIdle, { timeout: 3000 });
    } else {
      window.setTimeout(onIdle, 0);
    }
  };
  window.requestAnimationFrame(() => window.setTimeout(afterPaint, 0));
};
